import React from 'react';
import { isVowel } from '../../game';
import { CONSONANT_ROWS, formatMoney, VOWEL_KEYS } from '../../game/presentation';
import { VOWEL_COST } from '../../game';

interface LetterKeyboardProps {
  guessedLetters: string[];
  phrase: string;
  canGuessConsonant: boolean;
  canBuyVowel: boolean;
  onLetter: (letter: string) => void;
  disabled?: boolean;
}

const LetterKeyboard: React.FC<LetterKeyboardProps> = ({
  guessedLetters,
  phrase,
  canGuessConsonant,
  canBuyVowel,
  onLetter,
  disabled = false,
}) => {
  const guessed = new Set(guessedLetters);
  const puzzle = phrase.toLowerCase();

  const renderKey = (letter: string) => {
    const vowel = isVowel(letter);
    const used = guessed.has(letter);
    const inPuzzle = used && puzzle.includes(letter);
    const canPress = !disabled && !used && ((vowel && canBuyVowel) || (!vowel && canGuessConsonant));
    const className = [
      'ss-key',
      vowel ? 'is-vowel' : '',
      canPress ? 'is-on' : 'is-off',
      used && inPuzzle ? 'is-hit' : '',
      used && !inPuzzle ? 'is-miss' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <button
        key={letter}
        type="button"
        className={className}
        disabled={!canPress}
        onClick={() => onLetter(letter)}
        aria-label={
          vowel
            ? used
              ? `Vowel ${letter.toUpperCase()} already used`
              : `Buy vowel ${letter.toUpperCase()} for ${formatMoney(VOWEL_COST)}`
            : used
              ? `Letter ${letter.toUpperCase()} already guessed`
              : `Guess consonant ${letter.toUpperCase()}`
        }
      >
        {letter.toUpperCase()}
      </button>
    );
  };

  return (
    <div
      className={`ss-keys${canGuessConsonant || canBuyVowel ? ' is-live' : ''}${
        canGuessConsonant ? ' is-consonants' : ''
      }${canBuyVowel ? ' is-vowels' : ''}`}
      role="group"
      aria-label="Letter keyboard"
    >
      {CONSONANT_ROWS.map((row, index) => (
        <div className="ss-key-row cons" key={`c-${index}`}>
          {row.map(renderKey)}
        </div>
      ))}
      <div className="ss-key-row vowels" role="group" aria-label={`Vowels, ${formatMoney(VOWEL_COST)} each`}>
        {VOWEL_KEYS.map(renderKey)}
      </div>
    </div>
  );
};

export default LetterKeyboard;
